import { ComponentType, Dispatch, SetStateAction, SVGAttributes, useCallback } from 'react';

import PlcInteractiveIcon from '@/apps/mwd-v2/components/Legends/Left/PlcInteractiveIcon';
import { setScrollToTime } from '@/apps/mwd-v2/state/actions/scrollToTime';
import useMwdTimescale from '@/apps/mwd-v2/state/selectors/useMwdTimescale';
import { LeftLegendHoverArea } from '@/apps/mwd-v2/types/legends';
import { timeComparator } from '@/apps/mwd-v2/utils/timeComparator';
import DefectBeperktInzetbaarXs from '@/assets/icons/DefectBeperktInzetbaarXs';
import DefectInzetbaar from '@/assets/icons/DefectInzetbaar';
import DefectNietInzetbaarXs from '@/assets/icons/DefectNietInzetbaarXs';
import DefectZandloperXl from '@/assets/icons/DefectZandloperXl';
import TrainManco from '@/assets/icons/TrainManco';
import { useConflicten } from '@/state/selectors/useConflicten';
import { Eenheid } from '@/types/werklijnenInline';
import { bepaalDefectSoort } from '@/utils/helpers';
import { geefTijdstipVoorConflict, vindEersteInzet } from '@/views/ddp/utils/conflict';

import styles from './LegendLeft.module.css';

const legendIconMap: Record<string, ComponentType<SVGAttributes<SVGSVGElement>>> = {
  TrainManco,
  DefectNietInzetbaarXs,
  DefectZandloperXl,
  DefectBeperktInzetbaarXs,
  DefectInzetbaar,
};

type LeftLegendIconProps = {
  eenheid: Eenheid;
  veiligheidsconflict?: boolean;
  setHoverArea: Dispatch<SetStateAction<LeftLegendHoverArea>>;
  werklijnId: string;
};

export function bepaalIconClassName(iconStyle: string | undefined) {
  switch (iconStyle) {
    case 'TrainManco':
      return styles.legendLeftMancoIconStyle;
    case 'DefectNietInzetbaarXs':
      return styles.legendLeftDefectNietInzetbaarIconStyle;
    case 'DefectZandloperXl':
      return styles.legendLeftDefectNietInzetbaarZandloperIconStyle;
    case 'DefectBeperktInzetbaarXs':
      return styles.legendLeftDefectBeperktInzetbaarIconStyle;
    case 'DefectInzetbaar':
      return styles.legendLeftDefectInzetbaarIconStyle;
  }
}

export default function LeftLegendIcon({
  eenheid,
  veiligheidsconflict,
  setHoverArea,
  werklijnId,
}: Readonly<LeftLegendIconProps>) {
  let iconStyle: string | undefined;
  let iconClassName: string | undefined;

  const { scale } = useMwdTimescale();
  const { conflictenView: conflicten } = useConflicten(['PERRONLENGTE'], [werklijnId]);
  const conflict = conflicten.toSorted((a, b) => timeComparator(a, b, geefTijdstipVoorConflict)).at(0);
  const conflictElement = conflict?.elementen ? vindEersteInzet(conflict) : undefined;

  const handleIconMouseEvent = useCallback((area: LeftLegendHoverArea) => setHoverArea(area), []);

  const scrollToTime = useCallback(() => {
    if (conflictElement) {
      const x = scale(new Date(conflictElement.uitvoeringstijd));
      setScrollToTime(x);
    }
  }, [scale, conflictElement]);

  if (eenheid?.nummer === 'MANCO') {
    iconStyle = 'TrainManco';
    iconClassName = bepaalIconClassName(iconStyle);
  } else {
    iconStyle = bepaalDefectSoort(eenheid, new Date());
    iconClassName = bepaalIconClassName(iconStyle);
  }

  if (!iconStyle || !iconClassName) {
    return veiligheidsconflict ? (
      <PlcInteractiveIcon
        handleIconMouseEvent={handleIconMouseEvent}
        scrollToTime={scrollToTime}
        werklijnId={werklijnId}
        variant='big'
      />
    ) : null;
  }

  const IconComponent = legendIconMap[iconStyle];
  const plcInteractiveIcon = veiligheidsconflict ? (
    <PlcInteractiveIcon
      handleIconMouseEvent={handleIconMouseEvent}
      scrollToTime={scrollToTime}
      werklijnId={werklijnId}
      variant='small'
    />
  ) : null;

  if (iconStyle === 'DefectInzetbaar' || iconStyle === 'TrainManco' || iconStyle === 'DefectZandloperXl') {
    return (
      <div>
        {IconComponent && <IconComponent className={`${iconClassName} left-legend-icon`} width='24px' height='24px' />}
        {plcInteractiveIcon}
      </div>
    );
  }

  return (
    <div>
      <div className={`${iconClassName} left-legend-icon`}>
        {IconComponent && <IconComponent className={styles.icon} width='16px' height='16px' />}
      </div>
      {plcInteractiveIcon}
    </div>
  );
}
